import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { OAuthButtons } from '../components/OAuthButtons';
import { Turnstile, turnstileActive } from '../components/Turnstile';
import { isIntentPlan, savePlanIntent } from '../lib/intent';
import { oauthErrorMessage } from '../lib/rpc-client';
import { SaasError, saasResendVerification } from '../lib/saas-client';

function safeReturn(raw: string | null): string {
  if (!raw) return '/account';
  try {
    const decoded = decodeURIComponent(raw);
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded;
  } catch {
    // fall through
  }
  return '/account';
}

export function LoginPanel() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturn(params.get('return'));
  const resetOk = params.get('reset') === 'ok';
  const oauthError = params.get('oauth_error');
  const plan = params.get('plan');
  useEffect(() => {
    if (isIntentPlan(plan)) savePlanIntent(plan);
  }, [plan]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unverified, setUnverified] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendToken, setResendToken] = useState('');
  const [token, setToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [resendCaptchaKey, setResendCaptchaKey] = useState(0);

  if (user) return <Navigate to={returnTo} replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setUnverified(false);
    setResent(false);
    setBusy(true);
    try {
      await login(email.trim(), password, token || undefined);
      navigate(returnTo, { replace: true });
    } catch (err) {
      // Turnstile tokens are single-use; reissue a fresh one for the retry.
      setToken('');
      setCaptchaKey((k) => k + 1);
      if (err instanceof SaasError && err.code === 'email_unverified') {
        setUnverified(true);
      } else {
        setError(
          err instanceof SaasError && err.status === 401
            ? 'Incorrect email or password.'
            : err instanceof SaasError
              ? err.message
              : 'Sign-in failed.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setError(null);
    setResent(false);
    setBusy(true);
    try {
      await saasResendVerification(email.trim(), resendToken || undefined);
      setResent(true);
    } catch (err) {
      setResendToken('');
      setResendCaptchaKey((k) => k + 1);
      setError(err instanceof SaasError ? err.message : 'Could not resend the email.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <section className="panel auth-panel">
        <header className="page-header">
          <h1>Sign in</h1>
          <p>Manage your Willow RPC keys, usage, and billing.</p>
        </header>
        {oauthError && (
          <p className="form-error" data-testid="oauth-error">
            {oauthErrorMessage(oauthError)}
          </p>
        )}
        <OAuthButtons />
        {resetOk && (
          <p className="form-success">
            Password updated. Sign in with your new password.
          </p>
        )}
        {unverified && (
          <div className="form-error">
            <p style={{ margin: '0 0 0.4rem' }}>
              Verify your email before signing in. The link is in your inbox.
            </p>
            {resent ? (
              <span className="form-success">Verification email resent.</span>
            ) : (
              <>
                <Turnstile key={resendCaptchaKey} onVerify={setResendToken} />
                <button
                  type="button"
                  className="btn-link"
                  onClick={onResend}
                  disabled={busy || (turnstileActive && !resendToken)}
                >
                  {busy ? 'resending…' : 'Resend verification email'}
                </button>
              </>
            )}
          </div>
        )}
        <form onSubmit={onSubmit} className="auth-form">
          <label>
            <span>Email</span>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label>
            <span>Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {error && <p className="form-error">{error}</p>}
          <Turnstile key={captchaKey} onVerify={setToken} />
          <button
            type="submit"
            className="btn btn-primary"
            disabled={busy || (turnstileActive && !token)}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <p className="auth-switch">
          <Link to="/forgot-password">Forgot your password?</Link>
        </p>
        <p className="auth-switch" style={{ marginTop: '0.4rem', borderTop: 'none', paddingTop: 0 }}>
          New here?{' '}
          <Link to={`/signup?return=${encodeURIComponent(returnTo)}`} data-testid="to-signup">
            Get an API key
          </Link>
        </p>
      </section>
    </div>
  );
}
